import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { JwlTable, Chip, LoadingSpinner, ErrorComponent } from '../../../../components';
import { userLanguage } from '../../../../core/AuthContext';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { Link } from '@material-ui/core';

const PlatformMerchantContractList: React.FC = () => {
  const history = useHistory();
  const [errorComponentState, setErrorComponentState] = useState(false);

  const {
    data,
    loading,
    error,
    refetch
  } = GQLOperationBuilder.platformSaleCatalog.getPlatformSaleCatalogWithPlatformMerchantContracts({
    includeOnlyActiveContracts: true
  });

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests
  } = GQLOperationBuilder.request.getPlatformMerchantContractChangeRequestByPlatform(
    {
      completed: false
    },
    { fetchPolicy: 'network-only' }
  );

  if (loading || requestsLoading) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={refetch}
        message={error.message}
      />
    );

  const saleCatalogs = GQLOperationBuilder.platformSaleCatalog.platformSaleCatalogWithPlatformMerchantContractArrayFromResponse(
    data
  );
  const requests = GQLOperationBuilder.request.requestArrayFromResponse(requestsData);

  const requestsRows = () => {
    return requests.map(request => {
      return {
        id: request.id,
        cells: [
          request.request.platformMerchantContractChange?.previousContract.merchantParty.name,
          <Link
            onClick={() => {
              history.push(
                PlatformNavigation.platformSaleCatalogDetails.pathFromParams({
                  platformSaleCatalogId: request.request.platformMerchantContractChange!.previousContract
                    .platformSaleCatalogId
                })
              );
            }}
          >
            Details
          </Link>,

          <Link
            onClick={() => {
              history.push(
                PlatformNavigation.platformSaleCatalogDetails.pathFromParams({
                  platformSaleCatalogId: request.request.platformMerchantContractChange!.nextContract
                    .platformSaleCatalogId
                })
              );
            }}
          >
            Details
          </Link>,
          new Date(Date.parse(request.createdAt)).toLocaleString(),
          new Date(Date.parse(request.updatedAt)).toLocaleString(),
          new Date(Date.parse(request.validUntil)).toLocaleString(),
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={`${request.finishedAt ? 'YES' : 'PENDING'}`}
            $active={request.finishedAt && request.activities[0].approval?.accepted === true}
          />
        ]
      };
    });
  };

  const getMerchantContractsRows = () => {
    return saleCatalogs.map(merchantContract => {
      return {
        id: merchantContract?.platformMerchantContract?.id,
        cells: [
          merchantContract?.platformMerchantContract?.producerParty?.name,
          merchantContract?.platformMerchantContract?.producerDeliveryUnit?.name,
          merchantContract?.platformMerchantContract?.merchantParty?.name,
          merchantContract?.platformSaleCatalog?.name[userLanguage],
          merchantContract?.platformSaleCatalog?.description[userLanguage],
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={merchantContract.platformMerchantContractActive ? 'YES' : 'NO'}
            $active={merchantContract.platformMerchantContractActive}
          />,
          new Date(Date.parse(merchantContract?.platformMerchantContract?.updatedAt)).toLocaleString()
        ]
      };
    });
  };

  const navigateToMerchantContractsDetails = (merchantContractId: string) => {
    history.push(
      PlatformNavigation.platformMerchantContractDetails.pathFromParams({
        merchantContractId: merchantContractId
      })
    );
  };

  return (
    <React.Fragment>
      {requests.length > 0 && (
        <JwlTable
          title="Open requests for merchant contract change"
          description={`The platform pending requests for a platform <-> merchant contract change. Until a contract change request is accepted the existing contract still applies.`}
          columns={[
            'Merchant',
            'Previous catalog',
            'Next catalog',
            'Request created at',
            'Request updated at',
            'Request valid until',
            'State'
          ]}
          rows={requestsRows()}
        />
      )}

      <JwlTable
        title="Platform merchant contracts List"
        description="The platform contracts with specific merchants. Each merchant contract defines the platform commitments to the merchant in regards with pieces / accessories inventory & platform prices."
        columns={[
          'Producer Party Name',
          'Producer Delivery Unit',
          'Merchant Party Name',
          'Platform Sale Catalog Name',
          'Platform Sale Catalog Description',
          'Platform Merchant Contract Active',
          'Updated At'
        ]}
        rows={getMerchantContractsRows()}
        onTableRowClick={navigateToMerchantContractsDetails}
      />
    </React.Fragment>
  );
};

export default PlatformMerchantContractList;
